import React, { useCallback, useContext, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion/dist/framer-motion'

import { DataContext } from '../../Context/DataContext'
import { UIContext } from '../../Context/UIContext'

import { capitalizeFirstLetter } from '../../utils'
import useLockBodyScroll from '../../useLockBodyScroll'

import MenuButton from './MenuButton'
import HomeLink from '../HomeLink'

const SideMenu = () => {
    const { menuOpen, setMenuOpen, memberModalVisible, setMemberModalVisible } = useContext(UIContext)
    const { sectionsData, socials } = useContext(DataContext)
    const [isHovered, setIsHovered] = useState(false);

    useLockBodyScroll(menuOpen === true)

    const menu = sectionsData.map((section) => {
        if (section.identifier !== 'home') {
            return {
                title: section.title,
                slug: section.slug,
                xfn: section.identifier,
            }
        }
        return null
    }).filter(Boolean)

    const handleMenuHover = useCallback((isHovering) => {
        setIsHovered(isHovering);
    }, [setIsHovered]);

    const handleClose = useCallback(() => {
        setMenuOpen(false)
        if (memberModalVisible) {
            setMemberModalVisible(false)
        }
    }, [memberModalVisible, setMenuOpen, setMemberModalVisible])

    return (
        <AnimatePresence mode="wait">
            { menuOpen &&
                <motion.div
                    key="mga-sidemenu"
                    className={`mga-header__sidemenu ${isHovered ? 'mga-header__sidemenu--hover' : ''}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{
                        opacity: 0,
                        transition: { delay: 0.3, duration: 0.4 }
                    }}
                    transition={{delay: 0, duration: 0.4}}
                >
                    <div className="mga-header__sidemenu__overlay" onClick={handleClose} />
                    <motion.div
                        key="mga-sidemenu-wrapper"
                        className="mga-header__sidemenu__wrapper"
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{
                            opacity: 0,
                            x: '100%',
                            transition: { delay: 0, duration: 0.4 }
                        }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                        onMouseEnter={() => handleMenuHover(true)}
                        onMouseLeave={() => handleMenuHover(false)}
                    >
                        <HomeLink whitemode={true} minimal={true} />
                        <MenuButton whitemode={true} />
                        <nav className="mga-header__sidemenu__menu">
                            <ul>
                                {menu.map((item, index) => (
                                    <li key={`menu-item-${item.slug}`}>
                                        {item.xfn === 'external' ?
                                            <a 
                                                href={item.slug}
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                            >
                                                {item.title}
                                            </a>
                                        :
                                            <NavLink
                                                to={item.slug}
                                                onClick={handleClose}
                                                className={({ isActive, isPending }) =>
                                                    isPending ? 'pending' : isActive ? 'active' : ''
                                                }
                                            >
                                                {item.title}
                                            </NavLink>   
                                        }
                                    </li>
                                ))}
                            </ul>
                        </nav>
                        <div className="mga-header__sidemenu__bottom">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 375 34">
                                <path d="M-6645.574-2437.9s82.5-15.107,180.107-24.812,194.915-9.34,194.915-9.34v33.93Z" transform="translate(6645.574 2472.053)" />
                            </svg>
                            <ul className="mga-socials">
                                {Object.entries(socials).map(([key, value]) => (
                                    value && (
                                        <li
                                            key={`social-item-${key}`}
                                            className={`mga-socials__item mga-socials__item--${key}`}
                                        >
                                            <a 
                                                href={key === 'email' ? `mailto:${value}` : value}
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                            >
                                                {capitalizeFirstLetter(key)}
                                            </a>
                                        </li>
                                    )
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                </motion.div>
            } 
        </AnimatePresence>
    )
}

export default React.memo(SideMenu)